import { useQuery } from '@apollo/client'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { format, parseISO } from 'date-fns'
import { ALL_HOLIDAY_ALLOWANCES_QUERY } from '@/modules/companies/queries'
import type { HolidayAllowanceNode } from '@/modules/companies/types.graphql'
import { useConfirm, useNodes } from '@/utils/hooks'
import { OldAddButton, Header, OldSection, SectionHeader } from '../common'
import type { HolidayAllowanceForm } from './HolidayAllowancePrompt'
import { HolidayAllowancePrompt } from './HolidayAllowancePrompt'
import { useHolidayAllowanceMutations } from './hooks'
import { DataTable, FAIcon, Input } from '@/components'
import { createColumnHelper } from '@tanstack/react-table'
import { useCallback, useMemo, useState } from 'react'

const column = createColumnHelper<HolidayAllowanceNode>()

export const HolidayAllowanceSettings: React.JSX.CFC = () => {
  const translations = useTranslate({
    prompt: {
      delete: ['settings.prompts.delete', { name: '' }],
      deleteTitle: 'settings.prompts.holiday-allowance-delete-title',
    },
    repeatAnnually: 'common.repeat-annually',
    salaryCode: 'common.salary-code',
    holidayAllowances: 'common.holiday-allowances',
    date: 'common.date',
    noResults: 'common.no-results',
    search: 'common.search',
  })

  const addPrompt = usePrompt()
  const confirm = useConfirm()
  const mutations = useHolidayAllowanceMutations()

  const { data } = useQuery(ALL_HOLIDAY_ALLOWANCES_QUERY)
  const holidayAllowances = useNodes(data?.allHolidayAllowances)
  const [search, setSearch] = useState('')

  const filteredHolidayAllowances = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()
    if (!normalizedSearch) return holidayAllowances

    return holidayAllowances.filter(holidayAllowance =>
      [
        format(parseISO(holidayAllowance.day), 'dd.MM.yyyy'),
        holidayAllowance.salaryCode?.name,
        holidayAllowance.salaryCode?.externalId,
        holidayAllowance.repeatAnnually ? translations.repeatAnnually : '',
      ]
        .filter((value): value is string => Boolean(value))
        .some(value => value.toLowerCase().includes(normalizedSearch))
    )
  }, [holidayAllowances, search, translations.repeatAnnually])

  async function handleCreateHolidayAllowance() {
    const { data } = await addPrompt<HolidayAllowanceForm | null>(resolve => (
      <HolidayAllowancePrompt onSubmit={resolve} />
    ))
    if (data === null || data.salaryCodeId === null) return

    void mutations.createHolidayAllowance({
      variables: {
        input: {
          salaryCode: data.salaryCodeId,
          day: format(data.day, 'yyyy-MM-dd'),
          start: format(data.start, 'HH:mm'),
          end: format(data.end, 'HH:mm'),
          override: data.override,
          repeatAnnually: data.repeatAnnually,
        },
      },
    })
  }

  const handlePatchHolidayAllowance = useCallback(
    async (holidayAllowance: HolidayAllowanceNode) => {
      const { data } = await addPrompt<HolidayAllowanceForm | null>(resolve => (
        <HolidayAllowancePrompt
          initialData={holidayAllowance}
          onSubmit={resolve}
        />
      ))
      if (data === null || data.salaryCodeId === null) return

      void mutations.patchHolidayAllowance({
        variables: {
          id: holidayAllowance.id,
          input: {
            salaryCode: data.salaryCodeId,
            day: format(data.day, 'yyyy-MM-dd'),
            start: format(data.start, 'HH:mm'),
            end: format(data.end, 'HH:mm'),
            override: data.override,
            repeatAnnually: data.repeatAnnually,
          },
        },
      })
    },
    [addPrompt, mutations]
  )

  const handleDeleteHolidayAllowance = useCallback(
    async (holidayAllowance: HolidayAllowanceNode) => {
      const { data: answer } = await confirm(
        translations.prompt.delete({
          name:
            holidayAllowance.salaryCode?.name +
            ' ' +
            format(parseISO(holidayAllowance.day), 'dd.MM.yyyy'),
        }),
        translations.prompt.deleteTitle
      )
      if (!answer) return

      void mutations.deleteHolidayAllowance({
        variables: {
          id: holidayAllowance.id,
        },
      })
    },
    [confirm, mutations, translations.prompt]
  )

  const tableColumns = useMemo(
    () => [
      column.accessor('day', {
        header: translations.date,
        enableSorting: true,
        cell(info) {
          return <span>{format(parseISO(info.getValue()), 'dd.MM.yyyy')}</span>
        },
      }),
      column.accessor('salaryCode.name', {
        header: translations.salaryCode,
        enableSorting: true,
        cell(info) {
          return <span>{info.getValue() ?? '-'}</span>
        },
      }),
      column.accessor('repeatAnnually', {
        header: translations.repeatAnnually,
        enableSorting: true,
        meta: {
          narrow: true,
          center: true,
        },
        cell(info) {
          return info.getValue() ? (
            <FAIcon className="text-green-500" icon="check" />
          ) : (
            <FAIcon className="text-red-500" icon="times" />
          )
        },
      }),
      column.display({
        id: 'edit',
        meta: {
          narrow: true,
        },
        cell(info) {
          const holidayAllowance = info.row.original

          return (
            <FAIcon
              className="text-gray-400 hover:text-secondary"
              icon="edit"
              cursor="pointer"
              onClick={() => handlePatchHolidayAllowance(holidayAllowance)}
            />
          )
        },
      }),
      column.display({
        id: 'delete',
        meta: {
          narrow: true,
        },
        cell(info) {
          const holidayAllowance = info.row.original

          return (
            <FAIcon
              className="text-gray-400 hover:text-red-500"
              icon="times"
              cursor="pointer"
              onClick={() => handleDeleteHolidayAllowance(holidayAllowance)}
            />
          )
        },
      }),
    ],
    [
      handleDeleteHolidayAllowance,
      handlePatchHolidayAllowance,
      translations.date,
      translations.repeatAnnually,
      translations.salaryCode,
    ]
  )

  return (
    <OldSection>
      <Header>
        <SectionHeader>{translations.holidayAllowances}</SectionHeader>
        <OldAddButton adding={false} onClick={handleCreateHolidayAllowance} />
      </Header>

      <div className="mb-4 max-w-sm">
        <Input
          aria-label={translations.search}
          placeholder={translations.search}
          type="search"
          value={search}
          onChange={setSearch}
        />
      </div>

      <DataTable
        columns={tableColumns}
        data={filteredHolidayAllowances}
        noDataText={translations.noResults}
        pagination={{ pageSize: 20 }}
      />
    </OldSection>
  )
}
